import { useEffect, useState } from 'react';
import { type Listing } from '~/src/lib/types';
import { getBreadcrumbPath } from '~/src/lib/navigation-config';
import { supabase } from '~/src/integrations/supabase/client';
import { CANONICAL_SITE_URL } from '~/src/lib/part-indexability';
import { canonicalPageUrl } from '~/src/lib/canonical-url';

const DEFAULT_PAGE_TITLE = 'Industrial Automation Parts Search | Control Exchange';
const DEFAULT_META_DESCRIPTION = 'Search industrial automation parts by manufacturer part number. Compare PLC, HMI and drive prices, availability and supplier source links.';
const SITE_DESCRIPTION = 'Search and compare PLC and industrial automation parts across supplier websites with Control Exchange Market Scan.';

function formatPageTitle(title?: string): string {
  const trimmedTitle = title?.trim();
  if (!trimmedTitle) return DEFAULT_PAGE_TITLE;
  return /\bControl Exchange\b/i.test(trimmedTitle)
    ? trimmedTitle
    : `${trimmedTitle} | Control Exchange`;
}

interface SEOHeadProps {
  listing?: Listing;
  title?: string;
  description?: string;
  type?: 'website' | 'product';
  keywords?: string[];
  image?: string;
  canonicalUrl?: string;
  noindex?: boolean;
}

const SEOHead = ({ 
  listing, 
  title, 
  description, 
  type = 'website', 
  keywords = [], 
  image,
  canonicalUrl,
  noindex = false 
}: SEOHeadProps) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const toUserHandle = (username?: string | null) => {
    if (typeof username !== "string") return null;
    const trimmed = username.trim();
    if (!trimmed) return null;
    return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
  };

  // Fetch reviews for the listing
  useEffect(() => {
    if (listing?.id) {
      const fetchReviews = async () => {
        try {
          const { data, error } = await supabase
            .from('product_reviews')
            .select('*')
            .eq('listing_id', listing.id)
            .order('created_at', { ascending: false });
          
          if (!error && data) {
            setReviews(data);
          }
        } catch (error) {
          console.error('Error fetching reviews:', error);
        }
      };
      
      fetchReviews();
    }
  }, [listing?.id]);

  useEffect(() => {
    // Get breadcrumb path for current route
    const breadcrumbPath = getBreadcrumbPath(window.location.pathname);
    
    // Set document title
    const pageTitle = listing 
      ? `${listing.title} - ${listing.brand} ${listing.modelNumber} | Control Exchange`
      : formatPageTitle(title);
    
    document.title = pageTitle;
    
    const sellerHandle = listing
      ? (listing.seller.name || toUserHandle(listing.seller.username) || 'Seller')
      : 'Seller';

    // Set meta description
    const metaDescription = listing
      ? `Buy ${listing.brand} ${listing.modelNumber} - ${listing.condition} condition, ${listing.tested ? 'tested' : 'untested'} PLC part. £${(listing.priceCents / 100).toFixed(2)} from partnership seller ${sellerHandle}. Fast shipping available.`
      : description
      ? description
      : DEFAULT_META_DESCRIPTION;
    
    // Update or create meta tags
    const updateMetaTag = (property: string, content: string, isName = false) => {
      const attribute = isName ? 'name' : 'property';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', metaDescription, true);
    
    // Combine keywords from navigation config and custom keywords
    const defaultKeywords = listing
      ? `${listing.brand}, ${listing.modelNumber}, PLC, industrial automation, ${listing.condition}, used parts, control systems`
      : breadcrumbPath.length > 0 
        ? breadcrumbPath.map(item => item.keywords || []).flat().join(', ')
        : 'PLC parts price comparison, industrial automation parts, compare PLC prices, automation suppliers, Siemens parts, Allen-Bradley parts';
    
    const allKeywords = [...new Set([...defaultKeywords.split(', '), ...keywords])].join(', ');
    updateMetaTag('keywords', allKeywords, true);
    
    // Robots meta tags (general + bot-specific)
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow', true);
      updateMetaTag('googlebot', 'noindex, nofollow', true);
      updateMetaTag('bingbot', 'noindex, nofollow', true);
    } else {
      const crawlDirectives = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
      updateMetaTag('robots', crawlDirectives, true);
      updateMetaTag('googlebot', crawlDirectives, true);
      updateMetaTag('bingbot', crawlDirectives, true);
    }

    // Align client-rendered metadata with server pages and published sitemaps.
    const url = new URL(canonicalPageUrl(canonicalUrl || window.location.href));
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', url.toString());

    // Open Graph tags
    updateMetaTag('og:title', pageTitle);
    updateMetaTag('og:description', metaDescription);
    updateMetaTag('og:type', type);
    updateMetaTag('og:url', url.toString());
    updateMetaTag('og:site_name', 'Control Exchange');
    updateMetaTag('og:locale', 'en_GB');

    // hreflang tags for international SEO
    const ensureLinkTag = (rel: string, hreflang: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"][hreflang="${hreflang}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('hreflang', hreflang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    ensureLinkTag('alternate', 'en-GB', url.toString());
    ensureLinkTag('alternate', 'x-default', url.toString());
    
    // Open Graph image
    const ogImage = image || listing?.images?.[0] || `${CANONICAL_SITE_URL}/og.png`;
    updateMetaTag('og:image', ogImage);
    updateMetaTag('og:image:alt', listing ? `${listing.brand} ${listing.modelNumber}` : 'Control Exchange Market Scan');
    updateMetaTag('og:image:width', '1024');
    updateMetaTag('og:image:height', '1024');

    // Twitter Card tags
    updateMetaTag('twitter:card', listing?.images?.[0] || image ? 'summary_large_image' : 'summary', true);
    updateMetaTag('twitter:title', pageTitle, true);
    updateMetaTag('twitter:description', metaDescription, true);
    updateMetaTag('twitter:image', ogImage, true);
    updateMetaTag('twitter:image:alt', listing ? `${listing.brand} ${listing.modelNumber}` : 'Control Exchange Market Scan', true);

    // Open Graph Product-specific tags when applicable
    if (listing && type === 'product') {
      updateMetaTag('product:price:amount', (listing.priceCents / 100).toFixed(2));
      updateMetaTag('product:price:currency', listing.currency);
      updateMetaTag('product:availability', listing.quantity > 0 ? 'in stock' : 'out of stock');
    }

    // Structured data
    // Remove all existing JSON-LD to avoid duplicates across route changes
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    if (listing && type === 'product') {
      // Build valid GTIN if we have one (numeric and correct length: 8/12/13/14)
      const digitsOnly = (listing.modelNumber || '').replace(/[^0-9]/g, '');
      const isValidGtin = [8, 12, 13, 14].includes(digitsOnly.length) && digitsOnly.length > 0;

      // Derive GTIN property name (gtin8/12/13/14) when possible so Google doesn't flag invalid GTINs
      const gtinProperty = digitsOnly.length === 8
        ? 'gtin8'
        : digitsOnly.length === 12
          ? 'gtin12'
          : digitsOnly.length === 13
            ? 'gtin13'
            : digitsOnly.length === 14
              ? 'gtin14'
              : 'gtin';

      // Map marketplace condition to schema.org itemCondition value
      const itemCondition = {
        new: 'https://schema.org/NewCondition',
        used: 'https://schema.org/UsedCondition',
        refurbished: 'https://schema.org/RefurbishedCondition',
        factory_sealed: 'https://schema.org/NewCondition',
        unknown: 'https://schema.org/UsedCondition',
      }[listing.condition] ?? 'https://schema.org/UsedCondition';

      const brandName = (listing.brand || '').trim() || sellerHandle;
      const brandId = `${CANONICAL_SITE_URL}/brand/${encodeURIComponent(brandName.toLowerCase().replace(/\s+/g, '-'))}`;
      const manufacturerName = brandName !== sellerHandle ? brandName : undefined;

      // Return policy mapping: support no-returns via listing flag when available
      const noReturns = listing.returnsAllowed === false;
      const returnPolicyCategory = noReturns
        ? 'https://schema.org/MerchantReturnNotPermitted'
        : 'https://schema.org/MerchantReturnFiniteReturnWindow';

      // Shipping rate (required for some Merchant listings). Use priceCurrency and price as MonetaryAmount.
      const shippingMonetaryAmount = {
        '@type': 'MonetaryAmount',
        currency: listing.currency,
        value: listing.shippingCost ? Number((listing.shippingCost / 100).toFixed(2)) : 0,
      } as const;

      const deliveryTime = {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 2,
          unitCode: 'd',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 2,
          maxValue: 5,
          unitCode: 'd',
        },
      } as const;

      // Build @graph including Product + Organization + WebSite + Breadcrumbs
      const productNode: any = {
        '@type': 'Product',
        '@id': `${url.toString()}#product`,
        name: `${listing.brand} ${listing.modelNumber}`,
        description: listing.description || `${listing.brand} ${listing.modelNumber} - ${listing.condition} condition PLC part`,
        brand: { '@type': 'Brand', '@id': brandId, name: brandName },
        ...(manufacturerName ? { manufacturer: { '@type': 'Organization', name: manufacturerName } } : {}),
        model: listing.modelNumber,
        mpn: listing.modelNumber,
        sku: listing.id,
        productID: listing.id,
        category: 'Industrial Automation Equipment',
        image: listing.images && listing.images.length > 1 ? listing.images : listing.images?.[0] || undefined,
        url: url.toString(),
        keywords: `${listing.brand}, ${listing.modelNumber}, PLC, industrial automation, ${listing.condition}`,
        itemCondition,
        releaseDate: listing.createdAt,
        offers: {
          '@type': 'Offer',
          price: (listing.priceCents / 100).toFixed(2),
          priceCurrency: listing.currency,
          availability: listing.quantity > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
          seller: { '@type': 'Organization', name: sellerHandle },
          priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          url: url.toString(),
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'GB' },
            shippingRate: shippingMonetaryAmount,
            deliveryTime,
          },
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'GB',
            returnPolicyCategory,
            ...(noReturns ? {} : { merchantReturnDays: 14 }),
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn',
            inStoreReturnsOffered: false,
          },
        },
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Tested', value: listing.tested ? 'Yes' : 'No' },
          { '@type': 'PropertyValue', name: 'Quantity Available', value: String(listing.quantity) },
        ],
      };

      // Add aggregate rating if reviews exist
      if (reviews.length > 0) {
        const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
        const averageRating = totalRating / reviews.length;
        
        productNode.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: averageRating.toFixed(1),
          reviewCount: reviews.length,
          bestRating: 5,
          worstRating: 1
        };

        // Add individual reviews (limit to first 5 for performance)
        productNode.review = reviews.slice(0, 5).map(review => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: 'Verified Buyer' // Anonymize for privacy
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: review.rating,
            bestRating: 5,
            worstRating: 1
          },
          reviewBody: review.comment || `${review.rating} star review`,
          datePublished: review.created_at,
          ...(review.title && { name: review.title })
        }));
      }

      // Only add GTIN if it's a valid length and actually numeric
      if (isValidGtin && digitsOnly.match(/^\d+$/)) {
        productNode[gtinProperty] = digitsOnly;
      }

      if (listing.firmwareVersion) {
        productNode.additionalProperty.push({ '@type': 'PropertyValue', name: 'Firmware Version', value: listing.firmwareVersion });
      }

      const graph = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebSite',
            url: CANONICAL_SITE_URL,
            name: 'Control Exchange',
            description: metaDescription,
          },
          {
            '@type': 'Organization',
            url: CANONICAL_SITE_URL,
            name: 'Control Exchange',
            logo: `${CANONICAL_SITE_URL}/logo.png`,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Browse Parts', item: { '@type': 'WebPage', '@id': `${CANONICAL_SITE_URL}/browse` } },
              { '@type': 'ListItem', position: 2, name: listing.brand, item: { '@type': 'WebPage', '@id': brandId } },
              { '@type': 'ListItem', position: 3, name: `${listing.brand} ${listing.modelNumber}`, item: { '@type': 'WebPage', '@id': url.toString() } },
            ],
          },
          productNode,
        ],
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(graph);
      document.head.appendChild(script);

      // FAQ schema for product pages - common buyer questions
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `What condition is the ${listing.brand} ${listing.modelNumber}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `This ${listing.brand} ${listing.modelNumber} is in ${listing.condition} condition${listing.tested ? ' and has been tested to confirm functionality' : ''}. ${listing.quantity > 1 ? `There are ${listing.quantity} units available.` : ''}`
            }
          },
          {
            '@type': 'Question',
            name: `Does the ${listing.brand} ${listing.modelNumber} come with buyer protection?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, all purchases on Control Exchange include Buyer Protection. Your payment is held in escrow until you confirm receipt and are satisfied with the item. If there is an issue, our dispute resolution process ensures you are protected.'
            }
          },
          {
            '@type': 'Question',
            name: `Can I return the ${listing.brand} ${listing.modelNumber}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: listing.returnsAllowed
                ? 'Yes, this seller accepts returns within 14 days of delivery. The item must be returned in its original condition.'
                : 'This seller does not accept returns on this item. However, all purchases are covered by Buyer Protection, so you can raise a dispute if the item is not as described.'
            }
          },
          {
            '@type': 'Question',
            name: `How long does shipping take for the ${listing.brand} ${listing.modelNumber}?`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Delivery times depend on the seller, destination and carrier. Review the listing and carrier details before ordering.'
            }
          }
        ]
      };
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-faq-seo', 'true');
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    } else {
      // Create site-wide structured data with breadcrumbs
      const structuredData = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebSite',
            '@id': `${CANONICAL_SITE_URL}/#website`,
            url: CANONICAL_SITE_URL,
            name: 'Control Exchange',
            alternateName: 'Control Exchange Market Scan',
            description: SITE_DESCRIPTION,
            inLanguage: 'en-GB',
            publisher: { '@id': `${CANONICAL_SITE_URL}/#organization` },
          },
          {
            '@type': 'Organization',
            '@id': `${CANONICAL_SITE_URL}/#organization`,
            url: CANONICAL_SITE_URL,
            name: 'Control Exchange',
            description: 'Control Exchange provides Market Scan, a free search and comparison tool for PLC and industrial automation parts, alongside a resale marketplace.',
            logo: {
              '@type': 'ImageObject',
              url: `${CANONICAL_SITE_URL}/logo.png`,
              width: 1024,
              height: 1024,
            },
            contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', availableLanguage: 'English' },
            sameAs: ['https://www.linkedin.com/company/control-exchange'],
          },
          {
            '@type': 'WebApplication',
            '@id': `${CANONICAL_SITE_URL}/#market-scan`,
            name: 'Market Scan',
            description: 'Search PLC and industrial automation part numbers across supplier websites, then compare prices, availability and source links.',
            url: `${CANONICAL_SITE_URL}/`,
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Any',
            browserRequirements: 'Requires JavaScript',
            inLanguage: 'en-GB',
            isAccessibleForFree: true,
            provider: { '@id': `${CANONICAL_SITE_URL}/#organization` },
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
            featureList: [
              'Search by manufacturer part number or product description',
              'Compare supplier prices and availability',
              'View official manufacturer product information',
              'Visit suppliers directly',
            ],
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbPath.map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.label,
              item: { '@type': 'WebPage', '@id': `${CANONICAL_SITE_URL}${item.path}` },
            })),
          },
        ],
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      // Remove dynamically added FAQ/hreflang elements
      document.querySelectorAll('script[data-faq-seo]').forEach(s => s.remove());
      document.querySelectorAll('link[hreflang]').forEach(l => l.remove());
    };
  }, [listing, title, description, type, keywords, image, canonicalUrl, noindex, reviews]);

  return null; // This component doesn't render anything
};

export default SEOHead;
